import { AxiosResponse } from 'axios';
import api from 'config/api.config';

// ==================== Interfaces ====================

// User subscription details
export interface UserSubscription {
  type: string;
  status: string;
  startDate: string;
  endDate: string;
  billingCycle?: string;
  tier?: number;
}

// User payment details
export interface UserPayment {
  totalPaid: number;
  paymentCount?: number;
  lastPaymentDate: string | null;
  lastPaymentAmount?: number;
  paymentMethod: string | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId?: string | null;
}

// User accounts summary
export interface UserAccountsSummary {
  total: number;
  active: number;
  inactive: number;
  locked: number;
}

// User activity summary
export interface UserActivitySummary {
  totalLikes: number;
  totalComments: number;
  totalFollows: number;
  totalMessages: number;
  totalStories: number;
  totalPosts: number;
}

// User jobs summary
export interface UserJobsSummary {
  total: number;
  completed: number;
  failed: number;
  scheduled: number;
  successRate: number;
}

// User rules summary
export interface UserRulesSummary {
  total: number;
  active: number;
}

// Detailed user from GET /api/admin/users/detailed
export interface DetailedUser {
  _id: string;
  name: string;
  email: string;
  profilePicture: string | null;
  joinDate: string;
  lastLogin: string | null;
  status: string;
  subscription: UserSubscription;
  payment: UserPayment;
  accounts: UserAccountsSummary;
  activitySummary: UserActivitySummary;
  jobs: UserJobsSummary;
  rules: UserRulesSummary;
}

// Response from GET /api/admin/users/detailed
export interface DetailedUsersResponse {
  users: DetailedUser[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasMore: boolean;
  };
  filters: {
    startDate: string | null;
    endDate: string | null;
    subscriptionType: string | null;
    status: string | null;
    search: string | null;
    sortBy: string;
    sortOrder: string;
  };
  summary: {
    totalUsers: number;
    activeUsers: number;
    trialUsers: number;
    expiredUsers: number;
  };
}

// Query params for GET /api/admin/users/detailed
export interface DetailedUsersQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  subscriptionType?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: 'created_on' | 'email' | 'accountsCount';
  sortOrder?: 'asc' | 'desc';
}

// Account rule setting
export interface AccountRuleSetting {
  actionId: string;
  actionName: string;
  friendlyName: string;
  category: string;
  isActive: boolean;
  limitUtilized: number;
  limit: number;
}

// Account profile details
export interface AccountProfile {
  followersCount: number;
  mediaCount: number;
  accountType: string;
}

// Account jobs details
export interface AccountJobsDetails {
  total: number;
  completed: number;
  failed: number;
  scheduled: number;
  successRate: number;
}

// Account proxy details
export interface AccountProxy {
  assigned: boolean;
  country: string | null;
  lastUpdated: string | null;
}

// Account stats
export interface AccountStats {
  likes: { total: number; periodTotal: number };
  comments: { total: number; periodTotal: number };
  follows: { total: number; periodTotal: number };
  messages: { total: number; periodTotal: number };
  stories: { total: number; periodTotal: number };
  posts: { total: number; periodTotal: number };
}

// Account activity stats (for user details endpoint)
export interface AccountActivityStats {
  likes: number;
  comments: number;
  follows: number;
  messages: number;
  stories: number;
  posts: number;
}

// Account details
export interface AccountDetails {
  _id: string;
  username: string;
  platform: string;
  profilePictureUrl: string | null;
  isActive: boolean;
  isLocked: boolean;
  createdOn: string;
  lastJobRun: string | null;
  nextJobTime: string | null;
  allJobsCompleted?: boolean;
  profile: AccountProfile;
  activityStats?: AccountActivityStats;
  rules: {
    total: number;
    active: number;
    settings: AccountRuleSetting[];
  };
  jobs: AccountJobsDetails;
  stats?: AccountStats;
  proxy?: AccountProxy;
}

// Full user info from GET /api/admin/users/:userId/details
export interface FullUserInfo {
  _id: string;
  name: string;
  email: string;
  profilePicture: string | null;
  phone: string | null;
  role: string;
  joinDate: string;
  lastLogin: string | null;
  isActive: boolean;
  isEmailVerified: boolean;
  language: string;
}

// Response from GET /api/admin/users/:userId/details
export interface UserDetailsResponse {
  user: FullUserInfo;
  subscription: UserSubscription;
  payment: UserPayment;
  accounts: AccountDetails[];
  summary: {
    totalAccounts: number;
    activeAccounts: number;
    totalJobs: number;
    completedJobs: number;
    totalRules: number;
    activeRules: number;
    activityTotals?: {
      likes: number;
      comments: number;
      follows: number;
      messages: number;
      stories: number;
      posts: number;
    };
  };
}

// Response from GET /api/admin/users/:userId/accounts
export interface UserAccountsResponse {
  user: {
    _id: string;
    name: string;
    email: string;
  };
  accounts: AccountDetails[];
  summary: {
    totalAccounts: number;
    activeAccounts: number;
    periodStats: {
      totalLikes: number;
      totalComments: number;
      totalFollows: number;
      totalMessages: number;
      totalStories: number;
      totalPosts: number;
      totalJobs: number;
    };
  };
  filters: {
    startDate: string | null;
    endDate: string | null;
  };
}

// Query params for GET /api/admin/users/:userId/accounts
export interface UserAccountsQueryParams {
  startDate?: string;
  endDate?: string;
}

// Activity log entry
export interface ActivityLog {
  _id: string;
  accountId: string;
  accountUsername: string;
  actionType: string;
  actionDescription: string;
  username: string;
  caption: string;
  comment: string;
  mediaId: string;
  createdAt: string;
  completedAt: string;
  jobId: string | null;
}

// Response from GET /api/admin/users/:userId/activity-logs
export interface ActivityLogsResponse {
  user: {
    _id: string;
    name: string;
  };
  logs: ActivityLog[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasMore: boolean;
  };
  filters: {
    actionType: string | null;
    accountId: string | null;
    startDate: string | null;
    endDate: string | null;
  };
}

// Query params for GET /api/admin/users/:userId/activity-logs
export interface ActivityLogsQueryParams {
  page?: number;
  limit?: number;
  actionType?: string;
  accountId?: string;
  startDate?: string;
  endDate?: string;
}

// Query params for GET /api/admin/users/export
export interface ExportUsersQueryParams {
  startDate?: string;
  endDate?: string;
}

// ==================== API Functions ====================

/**
 * Get all users with comprehensive details
 * GET /api/admin/users/detailed
 */
export const getDetailedUsers = async (params?: DetailedUsersQueryParams): Promise<DetailedUsersResponse> => {
  try {
    const queryParams: Record<string, any> = {
      page: params?.page || 1,
      limit: params?.limit || 20,
    };

    if (params?.search) queryParams.search = params.search;
    if (params?.subscriptionType) queryParams.subscriptionType = params.subscriptionType;
    if (params?.status) queryParams.status = params.status;
    if (params?.startDate) queryParams.startDate = params.startDate;
    if (params?.endDate) queryParams.endDate = params.endDate;
    if (params?.sortBy) queryParams.sortBy = params.sortBy;
    if (params?.sortOrder) queryParams.sortOrder = params.sortOrder;

    const response: AxiosResponse<DetailedUsersResponse> = await api.get(
      `/api/admin/users/detailed`,
      {
        params: queryParams
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch detailed users');
  }
};

/**
 * Get complete details for a single user
 * GET /api/admin/users/:userId/details
 */
export const getUserDetails = async (userId: string): Promise<UserDetailsResponse> => {
  try {
    const response: AxiosResponse<UserDetailsResponse> = await api.get(
      `/api/admin/users/${userId}/details`
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch user details');
  }
};

/**
 * Get all social media accounts for a specific user
 * GET /api/admin/users/:userId/accounts
 */
export const getUserAccounts = async (userId: string, params?: UserAccountsQueryParams): Promise<UserAccountsResponse> => {
  try {
    const queryParams: Record<string, any> = {};

    if (params?.startDate) queryParams.startDate = params.startDate;
    if (params?.endDate) queryParams.endDate = params.endDate;

    const response: AxiosResponse<UserAccountsResponse> = await api.get(
      `/api/admin/users/${userId}/accounts`,
      {
        params: queryParams
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch user accounts');
  }
};

/**
 * Get paginated activity logs for a user
 * GET /api/admin/users/:userId/activity-logs
 */
export const getUserActivityLogs = async (userId: string, params?: ActivityLogsQueryParams): Promise<ActivityLogsResponse> => {
  try {
    const queryParams: Record<string, any> = {
      page: params?.page || 1,
      limit: params?.limit || 20,
    };

    if (params?.actionType) queryParams.actionType = params.actionType;
    if (params?.accountId) queryParams.accountId = params.accountId;
    if (params?.startDate) queryParams.startDate = params.startDate;
    if (params?.endDate) queryParams.endDate = params.endDate;

    const response: AxiosResponse<ActivityLogsResponse> = await api.get(
      `/api/admin/users/${userId}/activity-logs`,
      {
        params: queryParams
      }
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch activity logs');
  }
};

/**
 * Export users report as file download
 * GET /api/admin/users/export
 */
export const exportUsersReport = async (params?: ExportUsersQueryParams): Promise<void> => {
  try {
    const queryParams: Record<string, any> = {};

    if (params?.startDate) queryParams.startDate = params.startDate;
    if (params?.endDate) queryParams.endDate = params.endDate;

    const response = await api.get(
      `/api/admin/users/export`,
      {
        headers: {
          'Accept': 'application/octet-stream',
        },
        params: queryParams,
        responseType: 'blob',
      }
    );

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'users-report.csv';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }

    // Create blob and trigger download
    const blob = new Blob([response.data], { type: response.headers['content-type'] || 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to export users report');
  }
};

export default {
  getDetailedUsers,
  getUserDetails,
  getUserAccounts,
  getUserActivityLogs,
  exportUsersReport,
};
